import { memo, useContext, useMemo } from 'react';
import Head from 'next/head';
import { Global } from '@emotion/react';
import { css } from '@emotion/css';

import { cleanHTMLInput } from '../../lib/cleanUserInput';
import { useCurrentRoute } from '../../lib/useCurrentRoute';
import { WWContext, type WWContextValue } from '../WWContext';
import { isWwsWeddingWebsiteV2, Maybe } from '../../types/graphql';
import { RegistryNote } from './RegistryNote';
import { isClient } from '../../lib/utils';

const APPLICATION = 'wedding website';

const globalStyles = css`
  :focus {
    outline: transparent;
  }

  ol,
  ul {
    list-style: initial;
    margin: initial;
    padding: initial;
  }
`;

const shouldShowNote = (note: Maybe<string> | undefined): boolean => {
  if (!note) {
    return false;
  }

  return Boolean(note.replace(/<[^>]*>/g, '').trim());
};

const registryWrapperStyles = ({
  isShowingRegistry,
}: {
  isShowingRegistry: boolean;
}): string => css`
  display: ${isShowingRegistry ? 'block' : 'none'};
  padding-bottom: inherit !important;

  min-height: 60px !important; // Leave space for the loading overlay

  // Registry page only: break out of Layout's <Content> 16px side padding so the
  // product grid spans the full width here (other pages keep their padding).
  width: calc(100% + 32px);
  margin-left: -16px;
  margin-right: -16px;

  // Changes the position of the loading overlay from fixed to absolute
  // so that it does not cover the entire page when the registry is not showing.
  > div:has(> svg) {
    position: absolute;
    z-index: 17;
  }
`;

interface RegistryTemplateProps {
  forceVisible?: boolean;
  standaloneNote?: boolean | undefined;
}

function RegistryTemplate({
  forceVisible = false,
  standaloneNote,
}: RegistryTemplateProps) {
  const { data } = useContext<WWContextValue>(WWContext);
  const currentRoute = useCurrentRoute();
  const isMounted = isClient();

  const isOnRegistryRoute = currentRoute === 'registry';
  const showPage = forceVisible || isOnRegistryRoute;

  const note = isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)
    ? data?.weddingWebsiteResult.registryNote
    : '';

  const showNote = shouldShowNote(note);

  const dangerouslySetInnerHTML = useMemo<{
    __html: string;
  }>(() => ({ __html: cleanHTMLInput(note) }), [note]);

  if (
    isWwsWeddingWebsiteV2(data?.weddingWebsiteResult) &&
    data?.weddingWebsiteResult.userUuid
  ) {
    return (
      <>
        <Head>
          <link href={process.env['REG_PRODUCT_GRID_CSS']} rel="stylesheet" />
          {isMounted && (
            <script
              id="registry-product-grid-script-1"
              type="text/javascript"
              src={process.env['REG_PRODUCT_GRID_JS'] ?? ''}
              async
            />
          )}
        </Head>

        <Global styles={globalStyles} />

        <RegistryNote
          standalone={standaloneNote}
          show={showNote && showPage}
          htmlContent={dangerouslySetInnerHTML}
        />

        <div
          id="products-grid-app-root"
          data-member-id={data.weddingWebsiteResult.userUuid}
          data-application={APPLICATION}
          className={registryWrapperStyles({
            /**
             * Conditionally display registry content
             * as an alternative to re-injecting registry
             * content on every page load.
             */
            isShowingRegistry: showPage,
          })}
        />
      </>
    );
  }

  return (
    <RegistryNote standalone={standaloneNote} show={showNote}>
      {note}
    </RegistryNote>
  );
}

export default memo(RegistryTemplate);
